import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { CONFIG_CAT_FLAGS } from "app/components/GlobalNavbar/constants";
import Loading from "app/storybookComponents/Loading";
import Toggle from "app/components/Toggle";
import { Dropdown } from "react-bootstrap";
import {
  getWeeklyCheckInCompanySettings,
  selectExecutiveWeeklyCheckInCompanyCustomList,
  selectWeeklyCheckInCompanySettings,
  setSampleWeeklyCheckInEmailReportModalState,
} from "app/containers/WeeklyCheckInMonitor/slice";
import {
  selectAllCompanyUsersById,
  selectCurrentUserAccountId,
  selectIsCurrentUserAdmin,
  selectConfigCatFlag,
  selectGetAllUserStatus,
  selectCurrentUserInfo,
} from "app/containers/Global/slice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  selectManagerAndDirectReportsByUserAccountId,
  getManagerAndDirectReports,
} from "app/containers/AdvancedCsvUpload/slice";
import {
  getNotificationSettingTitleAndDescription,
  getSuperiorNames,
} from "./helpers";
import { NotificationChannels, UserWeeklyCheckInSettings } from "./types";
import {
  getUserNotificationsAndEmailSettings,
  selectGetUserNotificationsAndEmailSettingsStatus,
  selectUpdateUserNotificationsAndEmailSettingsStatus,
  selectUserNotificationsAndEmailSettings,
  updateUserNotificationsAndEmailSettings,
  getPlatformNotificationSettings,
  updatePlatformNotificationSettings,
  selectPlatformNotificationSettings,
  selectGetPlatformNotificationSettingsStatus,
  selectUpdatePlatformNotificationSettingsStatus,
} from "./slice";
import {
  getCompanyWeeklyCheckInCustomList,
  selectGetCompanyWeeklyCheckInCustomListStatus,
  selectGetWeeklyCheckInCompanySettingsStatus,
} from "../WeeklyCheckInMonitor/slice";
import {
  selectAiFeaturesEnabled,
  selectCompanySettings,
  selectIntegrationStatus,
  fetchIntegrationStatus,
} from "../AdminConsole/slice";
import { INTEGRATION_LOOKUP } from "../AdminConsole/constants";
import {
  getWeeklyCheckInSettingDisabledDueToCompanySettingBanner,
  getWeeklyCheckInSettingDisabledDueToCompanyWideSettingBanner,
} from "../WeeklyCheckInMonitor/helpers";
import {
  WEEKLY_CHECK_IN_FEATURE_NAME,
  WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS,
} from "../WeeklyCheckInMonitor/constants";
import { CheckInEmailReportType } from "../WeeklyCheckInMonitor/types";
import {
  getCoachBoAvailableLanguages,
  selectAvailableCoachBoLanguages,
  updateUserCoachBoLanguage,
} from "../Chatbot/slice";
import SearchableDropdownAlt from "app/storybookComponents/SearchableInput/SearchableDropdownAlt";
import { NotificationReminderOptions } from "./constant";

const UserEmailAndNotificationSettings = () => {
  const dispatch = useAppDispatch();
  const getUserNotificationsAndEmailSettingsStatus = useAppSelector(
    selectGetUserNotificationsAndEmailSettingsStatus
  );
  const loggedInUserId = useAppSelector(selectCurrentUserAccountId);
  const updateUserNotificationsAndEmailSettingsStatus = useAppSelector(
    selectUpdateUserNotificationsAndEmailSettingsStatus
  );
  const currentUserInfo = useAppSelector(selectCurrentUserInfo);
  const getAllUsersStatus = useAppSelector(selectGetAllUserStatus);
  const isUserAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const userNotificationsAndEmailSettings = useAppSelector(
    selectUserNotificationsAndEmailSettings
  );
  const users = useAppSelector(selectAllCompanyUsersById);
  const managersAndDirectReportsByUserAccountId = useAppSelector(
    selectManagerAndDirectReportsByUserAccountId
  );
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );
  const executiveWeeklyCheckInCompanyCustomList = useAppSelector(
    selectExecutiveWeeklyCheckInCompanyCustomList
  );
  const weeklyCheckInConfigCatFlag = useAppSelector(
    selectConfigCatFlag(CONFIG_CAT_FLAGS.weeklyCheckIn.savedFlagName)
  );
  const areAIFeaturesEnabled = useAppSelector(selectAiFeaturesEnabled);
  const isTeamsIntegrationEnabled = useAppSelector(
    selectConfigCatFlag("teamsIntegration")
  );
  const getWeeklyCheckInCompanySettingsStatus = useAppSelector(
    selectGetWeeklyCheckInCompanySettingsStatus
  );
  const getCompanyWeeklyCheckInCustomListStatus = useAppSelector(
    selectGetCompanyWeeklyCheckInCustomListStatus
  );
  const platformSettings = useAppSelector(selectPlatformNotificationSettings);
  const getPlatformSettingsStatus = useAppSelector(
    selectGetPlatformNotificationSettingsStatus
  );
  const updatePlatformSettingsStatus = useAppSelector(
    selectUpdatePlatformNotificationSettingsStatus
  );
  const availableCoachBoLanguages = useAppSelector(
    selectAvailableCoachBoLanguages
  );
  const companySettings = useAppSelector(selectCompanySettings);
  const integrationStatus = useAppSelector(selectIntegrationStatus);

  useEffect(() => {
    dispatch(getUserNotificationsAndEmailSettings());
    dispatch(getManagerAndDirectReports());
    dispatch(getWeeklyCheckInCompanySettings());
    dispatch(getPlatformNotificationSettings());
    dispatch(getCoachBoAvailableLanguages());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchIntegrationStatus(INTEGRATION_LOOKUP.teams?.id));
  }, [dispatch]);

  const isUserManager = useMemo(() => {
    if (!currentUserInfo) {
      return false;
    }
    return !!managersAndDirectReportsByUserAccountId[
      currentUserInfo.userAccountId
    ]?.directReportRelationships?.length;
  }, [currentUserInfo, managersAndDirectReportsByUserAccountId]);

  const isExecutiveSettingVisible = useMemo(() => {
    if (
      weeklyCheckInCompanySettings?.hasWeeklyCheckInExecutiveReportEnabledForAllAdmins &&
      isUserAdmin
    ) {
      return true;
    }

    if (
      !weeklyCheckInCompanySettings?.hasWeeklyCheckInExecutiveReportEnabledForCustomList
    ) {
      return false;
    }

    return (
      !!loggedInUserId &&
      executiveWeeklyCheckInCompanyCustomList?.includes(loggedInUserId)
    );
  }, [
    isUserAdmin,
    weeklyCheckInCompanySettings,
    executiveWeeklyCheckInCompanyCustomList,
    loggedInUserId,
  ]);

  if (getUserNotificationsAndEmailSettingsStatus === "loading") {
    return <Loading />;
  }

  const getEnableWeeklyCheckInForDirectReportsValue = () => {
    if (
      weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany ||
      userNotificationsAndEmailSettings?.hasWeeklyCheckInsEnabledForReportingStructure ||
      userNotificationsAndEmailSettings?.hasWeeklyCheckInsEnabledForReportingStructureByAdmin ||
      userNotificationsAndEmailSettings?.hasWeeklyCheckInsEnabledForReportingStructureBySuperior
    ) {
      return "Your entire reporting structure";
    }

    if (
      userNotificationsAndEmailSettings?.hasWeeklyCheckInsEnabledForDirectReports ||
      userNotificationsAndEmailSettings?.hasWeeklyCheckInsEnabledForDirectReportsByAdmin
    ) {
      return "Your direct reports";
    }

    return "Not enabled by you";
  };

  const getWarningBannerFromJSXElement = (text: JSX.Element) => (
    <div className="gold warning-banner">
      <div className="d-flex align-items-center">
        <FontAwesomeIcon icon={["far", "lock-keyhole"]} className="me-2" />
        {text}
      </div>
    </div>
  );

  const getNavigateToAdminConsoleLink = (isAdmin: boolean) => {
    if (!isAdmin) {
      return "Please reach out to an admin to change these settings.";
    }

    return (
      <>
        Navigate to the{" "}
        <Link to="/AdminConsole/Organization%20Settings/Check-Ins">
          Admin Console
        </Link>{" "}
        to change these settings.
      </>
    );
  };

  const getEnableWeeklyCheckInForDirectReportsWarningBanner = () => {
    if (!weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabled) {
      return getWeeklyCheckInSettingDisabledDueToCompanySettingBanner(
        !!isUserAdmin
      );
    }

    if (
      weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany
    ) {
      return getWeeklyCheckInSettingDisabledDueToCompanyWideSettingBanner(
        !!isUserAdmin
      );
    }

    if (
      userNotificationsAndEmailSettings?.hasWeeklyCheckInsEnabledForReportingStructureByAdmin
    ) {
      return getWarningBannerFromJSXElement(
        <p>
          {WEEKLY_CHECK_IN_FEATURE_NAME}s have been <b>enabled</b> for your
          entire reporting structure by an admin.{" "}
          {getNavigateToAdminConsoleLink(!!isUserAdmin)}
        </p>
      );
    }

    if (
      userNotificationsAndEmailSettings?.hasWeeklyCheckInsEnabledForDirectReportsByAdmin
    ) {
      return getWarningBannerFromJSXElement(
        <p>
          {WEEKLY_CHECK_IN_FEATURE_NAME}s have been <b>enabled</b> for your
          direct reports by an admin.{" "}
          {getNavigateToAdminConsoleLink(!!isUserAdmin)}
        </p>
      );
    }

    if (
      userNotificationsAndEmailSettings?.hasWeeklyCheckInsEnabledForReportingStructureBySuperior
    ) {
      if (getAllUsersStatus === "loading") {
        return getWarningBannerFromJSXElement(
          <Loading size={25} color="#805100" />
        );
      }

      const { supervisorsWithEnabledReportingStructureCheckIns = [] } =
        userNotificationsAndEmailSettings ?? {};
      return getWarningBannerFromJSXElement(
        <p>
          {WEEKLY_CHECK_IN_FEATURE_NAME}s have been <b>enabled</b> for your
          entire reporting structure by{" "}
          {getSuperiorNames(
            supervisorsWithEnabledReportingStructureCheckIns,
            users
          )}
          . Please reach out to{" "}
          {supervisorsWithEnabledReportingStructureCheckIns.length > 1
            ? "these users"
            : "this user"}{" "}
          to change these settings.{" "}
          {isUserAdmin ? (
            <>
              Or navigate to the{" "}
              <Link to="/AdminConsole/Organization%20Settings/Check-Ins">
                Admin Console
              </Link>{" "}
              to change these settings.
            </>
          ) : null}
        </p>
      );
    }

    return null;
  };

  const getWeeklyCheckInExecutiveSetting = () => {
    if (
      !isExecutiveSettingVisible ||
      !weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabled
    ) {
      return null;
    }
    const isSettingDisabledDueToVisibilityDefault =
      weeklyCheckInCompanySettings?.visibilityDefault ===
      WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.PRIVACY;
    const isSettingEnabled =
      !isSettingDisabledDueToVisibilityDefault &&
      !!userNotificationsAndEmailSettings?.hasWeeklyExecutiveReportEmailsEnabled;
    return (
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Enable Executive Check-in Report Emails</h3>
          <p>
            Receive an email after each check-in period with organization-wide
            insights, themes, and sentiments. This report also includes
            department level updates, a spotlight for successes, and people to
            connect with for the period.
            Your reports are always accessible from the Check-In Activity Table
            under Email Reports.{" "}
            <span
              className="blue-link"
              onClick={() => {
                onOpenPreviewEmailModal("executive-report");
              }}
            >
              Preview email
            </span>
          </p>
        </div>
        <div className="setting-row-action">
          {getLockIcon(isSettingDisabledDueToVisibilityDefault)}
          <Toggle
            isOn={isSettingEnabled}
            handleToggle={() => {
              const hasWeeklyExecutiveReportEmailsEnabled =
                userNotificationsAndEmailSettings?.hasWeeklyExecutiveReportEmailsEnabled
                  ? 0
                  : 1;
              dispatch(
                updateUserNotificationsAndEmailSettings({
                  hasWeeklyExecutiveReportEmailsEnabled,
                })
              );
            }}
            disabled={isUpdateLoading}
          />
        </div>
      </div>
    );
  };

  const getWeeklyCheckInTeamLeaderSetting = () => {
    if (
      !userNotificationsAndEmailSettings?.isTeamLeader ||
      !weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabled
    ) {
      return null;
    }
    const isSettingDisabledDueToVisibilityDefault =
      weeklyCheckInCompanySettings?.visibilityDefault ===
      WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.PRIVACY;
    const isSettingEnabled =
      !isSettingDisabledDueToVisibilityDefault &&
      !!userNotificationsAndEmailSettings?.hasWeeklyTeamLeaderReportEmailsEnabled;
    return (
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Enable Team Leader Check-in Report Emails</h3>
          <p>
            Receive an email after each check-in period with key patterns,
            recommended actions, and a completion analysis based on check-ins
            completed by your team members. Your reports are always accessible
            from the Check-In Activity Table under Email Reports.{" "}
            <span
              className="blue-link"
              onClick={() => {
                onOpenPreviewEmailModal("team-leader-report");
              }}
            >
              Preview email
            </span>
          </p>
        </div>
        <div className="setting-row-action">
          {getLockIcon(isSettingDisabledDueToVisibilityDefault)}
          <Toggle
            isOn={isSettingEnabled}
            handleToggle={() => {
              const hasWeeklyTeamLeaderReportEmailsEnabled =
                userNotificationsAndEmailSettings?.hasWeeklyTeamLeaderReportEmailsEnabled
                  ? 0
                  : 1;
              dispatch(
                updateUserNotificationsAndEmailSettings({
                  hasWeeklyTeamLeaderReportEmailsEnabled,
                })
              );
            }}
            disabled={isUpdateLoading}
          />
        </div>
      </div>
    );
  };

  const getWeeklyCheckInSeniorManagerSetting = () => {
    if (
      !userNotificationsAndEmailSettings?.isSeniorManager ||
      !weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabled
    ) {
      return null;
    }
    const isSettingDisabledDueToVisibilityDefault =
      weeklyCheckInCompanySettings?.visibilityDefault !==
      WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.DEVELOPMENT;
    const isSettingEnabled =
      !isSettingDisabledDueToVisibilityDefault &&
      !!userNotificationsAndEmailSettings?.hasWeeklySeniorManagerReportEmailsEnabled;

    return (
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Enable Senior Manager Check-in Report Emails</h3>
          <p>
            Receive an email after each check-in period with key patterns,
            recommended actions, and a completion analysis based on check-ins
            completed by your direct reports and entire reporting structure.
            Your reports will always be accessible from the Check-In Activity
            Table under Email Reports.{" "}
            <span
              className="blue-link"
              onClick={() => {
                onOpenPreviewEmailModal("senior-manager-report");
              }}
            >
              Preview email
            </span>
          </p>
        </div>
        <div className="setting-row-action">
          {getLockIcon(isSettingDisabledDueToVisibilityDefault)}
          <Toggle
            isOn={isSettingEnabled}
            handleToggle={() => {
              const hasWeeklySeniorManagerReportEmailsEnabled =
                userNotificationsAndEmailSettings?.hasWeeklySeniorManagerReportEmailsEnabled
                  ? 0
                  : 1;
              dispatch(
                updateUserNotificationsAndEmailSettings({
                  hasWeeklySeniorManagerReportEmailsEnabled,
                })
              );
            }}
            disabled={
              isUpdateLoading || isSettingDisabledDueToVisibilityDefault
            }
          />
        </div>
      </div>
    );
  };

  const getLockIcon = (showing: boolean) =>
    showing ? (
      <FontAwesomeIcon
        icon={["far", "lock-keyhole"]}
        style={{ color: "#805100" }}
      />
    ) : null;

  const onEnableWeeklyCheckInForDirectReportsSelect = async (
    key: string | null
  ) => {
    const payload: Pick<
      UserWeeklyCheckInSettings,
      | "hasWeeklyCheckInsEnabledForReportingStructure"
      | "hasWeeklyCheckInsEnabledForDirectReports"
    > = {
      hasWeeklyCheckInsEnabledForReportingStructure: 0,
      hasWeeklyCheckInsEnabledForDirectReports: 0,
    };
    switch (key) {
      case "direct-reports":
        payload.hasWeeklyCheckInsEnabledForDirectReports = 1;
        break;
      case "your-entire-reports-structure":
        payload.hasWeeklyCheckInsEnabledForReportingStructure = 1;
        break;
      case "disabled":
      default:
        break;
    }
    await dispatch(updateUserNotificationsAndEmailSettings(payload));
    dispatch(getCompanyWeeklyCheckInCustomList({ forceRefresh: true }));
  };

  const onOpenPreviewEmailModal = (type: CheckInEmailReportType) =>
    dispatch(
      setSampleWeeklyCheckInEmailReportModalState({
        isOpen: true,
        type,
      })
    );

  const getEnableWeeklyCheckInForDirectReports = () => {
    if (!isUserManager) {
      return null;
    }

    // Hide for non-admins when company restricts check-in recipient editing
    if (
      !isUserAdmin &&
      weeklyCheckInCompanySettings?.nonAdminsCanEditCheckInRecipients === false
    ) {
      return null;
    }

    if (
      getWeeklyCheckInCompanySettingsStatus === "loading" ||
      getCompanyWeeklyCheckInCustomListStatus === "loading"
    ) {
      return <Loading size={25} />;
    }

    const isUpdateLoading =
      updateUserNotificationsAndEmailSettingsStatus === "loading";
    const warningBanner = getEnableWeeklyCheckInForDirectReportsWarningBanner();
    return (
      <>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Enable {WEEKLY_CHECK_IN_FEATURE_NAME}s For Your Reports</h3>
            <p>
              People with {WEEKLY_CHECK_IN_FEATURE_NAME}s enabled will receive
              an email from Coach Bo to check on work progress, share how
              they’re feeling, and report any successes or challenges at the end
              of each check-in period.{" "}
              <span
                className="blue-link"
                onClick={() => {
                  onOpenPreviewEmailModal("check-in-email");
                }}
              >
                Preview email
              </span>
            </p>
            {warningBanner ? null : (
              <p style={{ marginTop: "-2px" }}>
                <b>Note:</b> Some people reporting to you may already receive
                check-ins if enabled by admins or other leaders in your
                organization.
              </p>
            )}
          </div>
          <div className="setting-row-action">
            {getLockIcon(!!warningBanner)}
            <Dropdown onSelect={onEnableWeeklyCheckInForDirectReportsSelect}>
              <Dropdown.Toggle
                variant="light"
                disabled={isUpdateLoading || !!warningBanner}
              >
                {getEnableWeeklyCheckInForDirectReportsValue()}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey={"disabled"}>
                  Not enabled by you
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item eventKey={"direct-reports"}>
                  Your direct reports
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item eventKey={"your-entire-reports-structure"}>
                  Your entire reports structure
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
        {warningBanner}
      </>
    );
  };

  const getCoachBoReminderEmailsSetting = () => (
    <div className="setting-row">
      <div className="setting-row-description">
        <h3>Enable Coach Bo Reminder Emails</h3>
        <p>
          Allow emails from Coach Bo when you have a scheduled reminder to chat.
        </p>
      </div>
      <div className="setting-row-action">
        <Toggle
          isOn={
            !!userNotificationsAndEmailSettings?.hasCoachBoReminderEmailsEnabled
          }
          handleToggle={() => {
            const hasCoachBoReminderEmailsEnabled =
              userNotificationsAndEmailSettings?.hasCoachBoReminderEmailsEnabled
                ? 0
                : 1;
            dispatch(
              updateUserNotificationsAndEmailSettings({
                hasCoachBoReminderEmailsEnabled,
              })
            );
          }}
          disabled={isUpdateLoading}
        />
      </div>
    </div>
  );

  const getCoachBoSessionRecapEmailsSetting = () => (
    <div className="setting-row">
      <div className="setting-row-description">
        <h3>Enable Coach Bo Session Recap Emails</h3>
        <p>
          Allow emails summarizing your most recent conversation with Coach Bo,
          including any commitments mentioned.
        </p>
      </div>
      <div className="setting-row-action">
        <Toggle
          isOn={!!userNotificationsAndEmailSettings?.hasCoachBoEmailsEnabled}
          handleToggle={() => {
            const hasCoachBoEmailsEnabled =
              userNotificationsAndEmailSettings?.hasCoachBoEmailsEnabled
                ? 0
                : 1;
            dispatch(
              updateUserNotificationsAndEmailSettings({
                hasCoachBoEmailsEnabled,
              })
            );
          }}
          disabled={isUpdateLoading}
        />
      </div>
    </div>
  );

  const getCoachBoLanguageSetting = () => {
    const selectedLanguage =
      currentUserInfo?.coachBoLanguage ??
      companySettings?.coachBoLanguage ??
      "English";

    const options = availableCoachBoLanguages.map((language) => ({
      value: language,
      label: language,
    }));

    return (
      <div className="setting-row">
        <div className="setting-row-description">
          <h3>Coach Bo Language</h3>
          <p>
            Select the language Coach Bo will use when communicating with you.
          </p>
        </div>
        <div className="setting-row-action">
          <SearchableDropdownAlt
            options={options}
            onSelect={(language) =>
              dispatch(updateUserCoachBoLanguage(language))
            }
            selectedValue={selectedLanguage}
          />
        </div>
      </div>
    );
  };

  const getPlatformIntegrationSettings = () => {
    if (getPlatformSettingsStatus === "loading") {
      return <Loading size={25} />;
    }
    const teamsIntegrationProvider = INTEGRATION_LOOKUP.teams?.id;
    const hasTeamsIntegration =
      !!teamsIntegrationProvider &&
      !!integrationStatus[teamsIntegrationProvider]?.hasIntegration;

    if (
      !platformSettings ||
      !hasTeamsIntegration ||
      !isTeamsIntegrationEnabled
    ) {
      return null;
    }

    const { final, disableToggle } = platformSettings;

    const selectedReminderMinutes =
      platformSettings?.notification_minutes_before ?? 30;
    const isCalendarEnabled = !!platformSettings?.final?.calendar;

    return (
      <>
        <hr className="m-0" />
        <h3 style={{ color: "#53565A" }}>Platform Integrations</h3>

        {Object.keys(final).map((platform) => {
          // Type assertion to tell TypeScript this is a valid key
          const notificationPlatform = platform as keyof NotificationChannels;
          const isOn = final[notificationPlatform];
          const isLocked = disableToggle[notificationPlatform];
          const { title, description } =
            getNotificationSettingTitleAndDescription(platform);

          return (
            <div key={platform} className="setting-row">
              <div className="setting-row-description">
                <h3>{title}</h3>
                <p>{description}</p>
              </div>

              <div className="setting-row-action">
                {isLocked && (
                  <FontAwesomeIcon
                    icon={["far", "lock-keyhole"]}
                    style={{ color: "#805100", marginRight: 6 }}
                  />
                )}

                <Toggle
                  isOn={!!isOn}
                  disabled={
                    isLocked || updatePlatformSettingsStatus === "loading"
                  }
                  handleToggle={() => {
                    dispatch(
                      updatePlatformNotificationSettings({
                        platform: notificationPlatform,
                        value: isOn ? 0 : 1,
                      })
                    );
                  }}
                />
              </div>
            </div>
          );
        })}
        {isCalendarEnabled && (
          <div className="setting-row">
            <div className="setting-row-description">
              <h3>Notification Reminder Timing</h3>
              <p>
                Choose how long before a scheduled calendar event you want to
                receive notifications.
              </p>
            </div>

            <div className="setting-row-action">
              <Dropdown
                onSelect={(key) => {
                  if (!key) return;

                  dispatch(
                    updatePlatformNotificationSettings({
                      notification_minutes_before: Number(key),
                    })
                  );
                }}
              >
                <Dropdown.Toggle
                  variant="light"
                  disabled={updatePlatformSettingsStatus === "loading"}
                >
                  {selectedReminderMinutes} minutes before
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  {NotificationReminderOptions.map((opt) => (
                    <Dropdown.Item
                      key={opt.value}
                      eventKey={opt.value}
                      active={opt.value === selectedReminderMinutes}
                    >
                      {opt.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        )}
      </>
    );
  };

  const getWeeklyCheckInWarningBannerText = () => {
    const { isTeamLeader, isSeniorManager } =
      userNotificationsAndEmailSettings ?? {};

    if (
      weeklyCheckInCompanySettings?.visibilityDefault ===
      WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.PRIVACY
    ) {
      if (isTeamLeader && isSeniorManager) {
        return "Senior Manager Check-in Reports and Team Leader Check-in Reports cannot be enabled due to visibility defaults set by an admin. Please reach out to an admin to change these settings.";
      }
      if (isTeamLeader) {
        return "Team Leader Check-in Reports cannot be enabled due to visibility defaults set by an admin. Please reach out to an admin to change these settings.";
      }
    }

    if (
      weeklyCheckInCompanySettings?.visibilityDefault !==
        WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.DEVELOPMENT &&
      isSeniorManager
    ) {
      return "Senior Manager Check-in Reports cannot be enabled due to visibility defaults set by an admin. Please reach out to an admin to change these settings.";
    }

    return null;
  };

  const weeklyCheckInBannerText = getWeeklyCheckInWarningBannerText();
  const getWeeklyCheckInSettingSection = () => {
    if (!areAIFeaturesEnabled || !weeklyCheckInConfigCatFlag) return null;
    const enableWeeklyCheckInForDirectReportSetting =
      getEnableWeeklyCheckInForDirectReports();
    const weeklyCheckInExecutiveSetting = getWeeklyCheckInExecutiveSetting();
    const weeklyCheckInTeamLeaderSetting = getWeeklyCheckInTeamLeaderSetting();
    const weeklyCheckInSeniorManagerSetting =
      getWeeklyCheckInSeniorManagerSetting();

    if (
      !enableWeeklyCheckInForDirectReportSetting &&
      !weeklyCheckInExecutiveSetting &&
      !weeklyCheckInTeamLeaderSetting &&
      !weeklyCheckInSeniorManagerSetting
    ) {
      return null;
    }

    return (
      <>
        <hr />
        <h3 style={{ color: "#53565A" }}>{WEEKLY_CHECK_IN_FEATURE_NAME}s</h3>
        {enableWeeklyCheckInForDirectReportSetting}
        {weeklyCheckInExecutiveSetting}
        {weeklyCheckInTeamLeaderSetting}
        {weeklyCheckInSeniorManagerSetting}
        {weeklyCheckInBannerText && (
          <div className="warning-banner gold">
            <p>
              <FontAwesomeIcon
                icon={["far", "lock-keyhole"]}
                className="me-2"
              />
              {weeklyCheckInBannerText}
            </p>
          </div>
        )}
      </>
    );
  };

  const isUpdateLoading =
    updateUserNotificationsAndEmailSettingsStatus === "loading";
  return (
    <>
      <h3 style={{ color: "#53565A" }}>Coach Bo</h3>
      {getCoachBoReminderEmailsSetting()}
      {getCoachBoSessionRecapEmailsSetting()}
      {getCoachBoLanguageSetting()}
      {getPlatformIntegrationSettings()}
      {getWeeklyCheckInSettingSection()}
    </>
  );
};

export default UserEmailAndNotificationSettings;
